import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Star, MessageSquare, Reply, Trash2 } from 'lucide-react';

const RatingStars = ({ value = 0, size = 4, onChange }) => {
  const interactive = typeof onChange === 'function';
  return (
    <div className='inline-flex items-center gap-0.5' role={interactive ? 'radiogroup' : undefined}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type='button'
          disabled={!interactive}
          onClick={() => interactive && onChange(n)}
          className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
          data-testid={interactive ? `review-star-${n}` : undefined}
        >
          <Star
            className={`h-${size} w-${size} ${n <= value ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`}
          />
        </button>
      ))}
    </div>
  );
};

export const ReviewsWidget = ({ sellerId, isOwner = false, refreshKey = 0 }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWrite, setOpenWrite] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/reviews/seller/${sellerId}`)
      .then(r => setReviews(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadEligibility = () => {
    if (!user || user.id === sellerId) return;
    api.get(`/reviews/eligibility/${sellerId}`)
      .then(r => setEligibility(r.data))
      .catch(() => {});
  };

  // `refreshKey` lets the parent force an eligibility re-check without a page
  // reload — e.g. revealing a seller's contact makes the viewer eligible, and
  // the Write-a-review button must appear straight away.
  useEffect(() => { load(); loadEligibility(); /* eslint-disable-next-line */ }, [sellerId, user?.id, refreshKey]);

  const submit = async () => {
    if (!form.body || form.body.length < 10) return toast.error('Review must be at least 10 characters');
    setBusy(true);
    try {
      await api.post('/reviews', {
        seller_id: sellerId,
        rating: form.rating,
        title: form.title,
        body: form.body,
      });
      toast.success('Review posted');
      setOpenWrite(false); setForm({ rating: 5, title: '', body: '' });
      load(); loadEligibility();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post review');
    } finally { setBusy(false); }
  };

  const submitReply = async (reviewId) => {
    if (!replyText || replyText.length < 2) return toast.error('Reply too short');
    setBusy(true);
    try {
      await api.post(`/reviews/${reviewId}/reply`, { body: replyText });
      toast.success('Reply posted');
      setReplyingTo(null); setReplyText(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const removeReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast.success('Deleted'); load(); loadEligibility();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const canWrite = user && !isOwner && eligibility?.eligible;

  return (
    <div className='space-y-3' data-testid='reviews-widget'>
      <div className='flex items-end justify-between'>
        <h2 className='text-xl font-semibold'>Reviews {reviews.length > 0 && <span className='text-sm text-muted-foreground'>({reviews.length})</span>}</h2>
        {!isOwner && user && (
          canWrite ? (
            <Button size='sm' onClick={() => setOpenWrite(true)} data-testid='reviews-write-btn'>
              <MessageSquare className='h-4 w-4 mr-1.5' /> Write a review
            </Button>
          ) : eligibility && !eligibility.eligible ? (
            <span className='text-xs text-muted-foreground max-w-[420px] text-right'>
              {eligibility.reason}
            </span>
          ) : null
        )}
      </div>

      {loading ? (
        <div className='text-muted-foreground text-sm py-6 text-center'>Loading reviews…</div>
      ) : reviews.length === 0 ? (
        <Card className='p-8 text-center text-sm text-muted-foreground'>
          No reviews yet. Reviews are visible once a buyer has interacted with a listing of this business.
        </Card>
      ) : (
        <div className='space-y-3'>
          {reviews.map(r => (
            <Card key={r.id} className='p-5' data-testid={`review-row-${r.id}`}>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <div
                    className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'
                    data-testid={`review-header-${r.id}`}
                  >
                    <span className='font-medium text-foreground'>
                      {r.reviewer_name}
                    </span>
                    {r.reviewer_company && (
                      <span className='truncate'>· {r.reviewer_company}</span>
                    )}
                    <span
                      className='inline-flex items-center gap-1 rounded-full bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 px-2 py-0.5 text-[11px] font-semibold'
                      data-testid={`review-rating-chip-${r.id}`}
                      title={`Rated ${r.rating} out of 5`}
                    >
                      <RatingStars value={r.rating} size={3} />
                      <span>{r.rating}.0</span>
                    </span>
                    <span aria-hidden='true'>·</span>
                    <span>
                      {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  {r.title && <div className='font-semibold mt-1.5'>{r.title}</div>}
                  <p className='mt-2 text-sm leading-relaxed'>{r.body}</p>

                  {r.reply && (
                    <div className='mt-3 rounded-lg border-l-2 border-primary bg-secondary/40 p-3'>
                      <div className='text-xs text-muted-foreground mb-1 font-medium'>Seller reply</div>
                      <p className='text-sm leading-relaxed'>{r.reply}</p>
                    </div>
                  )}
                </div>
                <div className='flex flex-col gap-1'>
                  {isOwner && !r.reply && (
                    <Button size='sm' variant='outline' onClick={() => { setReplyingTo(r.id); setReplyText(''); }} data-testid={`reply-btn-${r.id}`}>
                      <Reply className='h-3.5 w-3.5 mr-1' /> Reply
                    </Button>
                  )}
                  {user && r.reviewer_id === user.id && (
                    <Button size='sm' variant='ghost' onClick={() => removeReview(r.id)}>
                      <Trash2 className='h-3.5 w-3.5' />
                    </Button>
                  )}
                </div>
              </div>
              {replyingTo === r.id && (
                <div className='mt-3 space-y-2'>
                  <Textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder='Write a professional reply…' data-testid={`reply-input-${r.id}`} />
                  <div className='flex gap-2 justify-end'>
                    <Button size='sm' variant='outline' onClick={() => setReplyingTo(null)}>Cancel</Button>
                    <Button size='sm' onClick={() => submitReply(r.id)} disabled={busy} data-testid={`reply-submit-${r.id}`}>Post reply</Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openWrite} onOpenChange={setOpenWrite}>
        <DialogContent>
          <DialogHeader><DialogTitle>Write a review</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label className='mb-1 block'>Rating</Label>
              <RatingStars value={form.rating} size={6} onChange={(n) => setForm({ ...form, rating: n })} />
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder='Summarise your experience' maxLength={120} />
            </div>
            <div>
              <Label>Details</Label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid='review-body-input' placeholder='Share what worked well and what could be improved. Be specific and factual.' maxLength={2000} />
              <div className='text-[11px] text-muted-foreground mt-1'>Min 10 characters. Do not include contact details or spam.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setOpenWrite(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid='review-submit-btn'>Post review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsWidget;
